import React, { useContext, useState } from 'react';
import { Loader2Icon, CheckCircle2Icon, XCircleIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ChartToolCall, ToolCallHandlersContext, ChartAutoRunContext } from '../assistant-ui/chart-tool-call';
import { SimilarColumnValuesToolCall } from '../assistant-ui/similar-column-values-tool-call';
import { ContextToolCall } from '../assistant-ui/context-tool-call';
import { SemanticLayerContextToolCall } from '../assistant-ui/semantic-layer-context-tool-call';
import { SourceTablesToolCall } from '../assistant-ui/source-tables-tool-call';
import { ApplyChangesToolCall } from '../assistant-ui/apply-changes-tool-call';
import { ResetWorkspaceToolCall } from '../assistant-ui/reset-workspace-tool-call';
import { CreateMetricToolCall } from '../assistant-ui/create-metric-tool-call';
import { ProcessMetricsToolCall } from '../assistant-ui/process-metrics-tool-call';
import { GetMetricJoinPathsToolCall } from '../assistant-ui/get-metric-join-paths-tool-call';
import { GetSampleDataToolCall } from '../assistant-ui/get-sample-data-tool-call';
import { GetColumnDistributionToolCall } from '../assistant-ui/get-column-distribution-tool-call';
import { CreateRelationshipToolCall } from '../assistant-ui/create-relationship-tool-call';
import { CreateDimensionToolCall } from '../assistant-ui/create-dimension-tool-call';
import { MapStagingToEntityToolCall } from '../assistant-ui/map-staging-to-entity-tool-call';
import { CreateEntityToolCall } from '../assistant-ui/create-entity-tool-call';
import { BuildNodesToolCall } from '../assistant-ui/build-nodes-tool-call';
import { CreateMultiSourceStagingNodeToolCall } from '../assistant-ui/create-multi-source-staging-node-tool-call';
import { CreateSingleSourceStagingNodeToolCall } from '../assistant-ui/create-single-source-staging-node-tool-call';
import { SetMetricJoinPathsToolCall } from '../assistant-ui/set-metric-join-paths-tool-call';
import { RunFullBuildToolCall } from '../assistant-ui/run-full-build-tool-call';
import { FileUploadToolCall } from '../assistant-ui/file-upload-tool-call';
import { GetStagingNodesToolCall } from '../assistant-ui/get-staging-nodes-tool-call';
import { SearchUnstructuredContextToolCall } from '../assistant-ui/search-unstructured-context-tool-call';
import { GetUnstructuredContextToolCall } from '../assistant-ui/get-unstructured-context-tool-call';
import { Button } from '../ui/button';

interface ToolCallProps {
  toolName: string;
  toolCallId: string;
  args: any;
  argsText: string;
  result?: string;
  status?: { type: 'running' | 'complete' | 'incomplete'; reason?: string };
}

// Registry for custom tool components
const toolComponentRegistry: Record<string, React.ComponentType<any>> = {
  generate_chart_input: ChartToolCall,
  generate_and_run_analysis: ChartToolCall,
  similar_column_values: SimilarColumnValuesToolCall,
  get_similar_column_values: SimilarColumnValuesToolCall,
  create_context_item: ContextToolCall,
  // Ruthie (Data Architect) tools
  get_semantic_layer_context: SemanticLayerContextToolCall,
  get_source_tables: SourceTablesToolCall,
  get_staging_nodes: GetStagingNodesToolCall,
  apply_changes_to_develop: ApplyChangesToolCall,
  reset_workspace: ResetWorkspaceToolCall,
  create_metric: CreateMetricToolCall,
  process_metrics: ProcessMetricsToolCall,
  get_metric_join_paths: GetMetricJoinPathsToolCall,
  get_sample_data_from_node: GetSampleDataToolCall,
  get_column_distribution: GetColumnDistributionToolCall,
  create_relationship: CreateRelationshipToolCall,
  create_dimension: CreateDimensionToolCall,
  map_staging_to_entity: MapStagingToEntityToolCall,
  create_entity: CreateEntityToolCall,
  build_nodes: BuildNodesToolCall,
  create_multi_source_staging_node: CreateMultiSourceStagingNodeToolCall,
  create_single_source_staging_node: CreateSingleSourceStagingNodeToolCall,
  set_metric_join_paths: SetMetricJoinPathsToolCall,
  run_full_build: RunFullBuildToolCall,
  request_file_upload: FileUploadToolCall,
  // Ellie unstructured context tools
  search_unstructured_context: SearchUnstructuredContextToolCall,
  get_unstructured_context: GetUnstructuredContextToolCall,
};

export const ToolCall = React.memo<ToolCallProps>(({
  toolName,
  toolCallId,
  args,
  argsText,
  result,
  status,
}) => {
  const handlers = useContext(ToolCallHandlersContext);
  const autoRunFromContext = useContext(ChartAutoRunContext);
  const [isExpanded, setIsExpanded] = useState(false);
  
  const isRunning = status?.type === 'running' || (status === undefined && result === undefined);
  const isError = status?.type === 'incomplete' && status.reason === 'error';
  const isComplete = !isRunning && !isError && result !== undefined;

  // Check if there's a custom component for this tool
  const CustomComponent = toolComponentRegistry[toolName];

  if (CustomComponent) {
    return (
      <CustomComponent
        toolName={toolName}
        toolCallId={toolCallId}
        args={args}
        argsText={argsText}
        result={result}
        status={status}
      />
    );
  }

  // Default tool call component
  return (
    <div className={cn(
      "rounded-lg border p-4 mb-4",
      isRunning && "border-muted bg-muted/50",
      isComplete && "border-success/50 bg-success/10",
      isError && "border-destructive/50 bg-destructive/10"
    )}>
      <div className="flex items-center gap-2 mb-2">
        {isRunning && <Loader2Icon className="h-4 w-4 animate-spin text-muted-foreground" />}
        {isComplete && <CheckCircle2Icon className="h-4 w-4 text-success" />}
        {isError && <XCircleIcon className="h-4 w-4 text-destructive" />}
        <span className="text-sm font-medium">
          {isRunning && 'Running tool: '}
          {isComplete && 'Tool completed: '}
          {isError && 'Tool error: '}
          <strong>{toolName}</strong>
        </span>
        {!isRunning && (
          <Button
            variant="ghost"
            size="sm"
            className="ml-auto h-6 px-2"
            onClick={() => setIsExpanded(!isExpanded)}
          >
            {isExpanded ? (
              <>
                <ChevronUpIcon className="h-3 w-3 mr-1" />
                Hide
              </>
            ) : (
              <>
                <ChevronDownIcon className="h-3 w-3 mr-1" />
                Show
              </>
            )}
          </Button>
        )}
      </div>
      {isExpanded && (
        <div className="mt-3 space-y-2">
          <div>
            <p className="text-xs font-medium text-muted-foreground mb-1">Arguments:</p>
            <pre className="text-xs bg-background p-2 rounded border overflow-x-auto">
              {argsText}
            </pre>
          </div>
          {result !== undefined && (
            <div>
              <p className="text-xs font-medium text-muted-foreground mb-1">Result:</p>
              <pre className="text-xs bg-background p-2 rounded border overflow-x-auto">
                {typeof result === 'string' ? result : JSON.stringify(result, null, 2)}
              </pre>
            </div>
          )}
          {isError && status?.reason && (
            <div>
              <p className="text-xs font-medium text-destructive mb-1">Error:</p>
              <p className="text-xs text-destructive">{status.reason}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}, (prev, next) => {
  return (
    prev.toolCallId === next.toolCallId &&
    prev.toolName === next.toolName &&
    prev.result === next.result &&
    prev.status?.type === next.status?.type &&
    prev.argsText === next.argsText
  );
});
ToolCall.displayName = 'ToolCall';

